import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { pdf } from '@app/core/constants';
import { DocumentLetterType } from '@app/core/model/document-letter-type.model';
import { NotificationService } from '@app/core/services';
import { DocumentsContextService, DocumentsUploadLetterContextService } from '@app/core/services/context/factory';
import { PlatformService } from '@app/core/services/context/platform.service';
import { SharedLineSelectComponent } from '@app/shared/components/shared-line-select/shared-line-select.component';
import {
  ICoreAuthorizationDTO,
  IServicingProviderDTO,
  UploadLetter,
} from '@core/model';
import { ShellService } from '@mynexus/mfe-core';
import { ProductNames, TableColumn, TenantNames } from '@mynexus/mynexus-ui-lib';
import { finalize } from 'rxjs';
import { DocumentTypesList } from '../models/document-types';

@Component({
    selector: 'mnc-documents-documents-upload-letter',
    templateUrl: './documents-upload-letter.component.html',
    styleUrls: ['./documents-upload-letter.component.scss'],
    standalone: false
})
export class DocumentsUploadLetterComponent implements OnInit, OnDestroy {
  @ViewChild(SharedLineSelectComponent) sharedLineSelectComponent!: SharedLineSelectComponent;

  @Input() authorization: ICoreAuthorizationDTO;
  @Input() tenantId!: string;
  @Input() tenantName!: TenantNames;
  @Input() servicingProvider!: IServicingProviderDTO;
  @Output() uploadLetterComplete = new EventEmitter<boolean>(false);
  @Output() closeNotes = new EventEmitter<void>();
  // Constants
  letterTypes: DocumentLetterType[] = [];
  allowedExtensions: string[] = [pdf];
  maxFileSize = 20 * 1024 * 1024; // 20Mb
  maxFileNameSize = 150;

  // Attachment values
  attachedFiles: UploadLetter[] = [];
  fileTypeErrors = false;
  fileSizeErrors = false;
  fileNameSizeErrors = false;
  fileNameInvalidErrors = false;

  // Line values
  @Input() lineData: object[] = [];
  selectedLineIds: string[] = [];
  enableLineSelection: boolean;
  isEligibleForUpload: boolean;

  // Form values
  form: FormGroup;
  selectedLetter = '';
  isUrgent = false;

  isUploading = false;

  columns: TableColumn[] = [];
  filteredLineData: object[] = [];
  isSingleSelect = false;

  private sub = this.getLetterTypes.bind(this);

  // diff between streams in using documentDisplayName & documentType
  private isHH!: boolean;
  authProviderPgpId = '';
  get noAttachments(): boolean {
    return this.attachedFiles.length === 0;
  }

  get noLines(): boolean {
    return this.filteredLineData.length === 0;
  }

  get showAttachButton(): boolean {
    return !!this.selectedLetter && this.noAttachments && (!this.noLines || (this.noLines && !this.enableLineSelection && this.isEligibleForUpload));
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly documentsContextService: DocumentsContextService,
    private readonly documentsUploadLetterContextService: DocumentsUploadLetterContextService,
    private readonly contextService: PlatformService,
    private notificationService: NotificationService,
    private fb: FormBuilder,
    private readonly shellService: ShellService,
  ) {
  }

  ngOnInit(): void {
    this.isHH = this.contextService.widgetContext === ProductNames.HomeHealth;

    if (this.documentsUploadLetterContextService) {
      this.getLetterTypes();
      this.columns = this.documentsUploadLetterContextService.getLineTableConfig();
      this.documentsUploadLetterContextService.getAuthorizationProvidersSummary(this.tenantId, this.authorization.authorizationId).subscribe(response => {
        this.authProviderPgpId = response?.['servicingProviderPgpId'];
      });
    }

    this.form = this.fb.group({
      letterType: ['', Validators.required],
      isUrgent: [this.isUrgent],
    });

    this.form.get('letterType')?.valueChanges.subscribe(async (value: DocumentLetterType) => {
      this.resetAttachments();
      this.form.get('isUrgent')?.setValue(false);
      this.sharedLineSelectComponent?.resetSelected();
      this.isSingleSelect = this.documentsUploadLetterContextService.isSingleLineSelection(value.documentDisplayName);
      this.filteredLineData = this.documentsUploadLetterContextService.mapLineTableData(
        this.isHH ? value.documentType : value.documentDisplayName,
        this.lineData,
        this.tenantName,
        this.authorization,
        this.servicingProvider,
        this.authProviderPgpId
      );
      this.selectedLetter = value.documentDisplayName;

      // todo: for HH those props hardcoded in mapping letterTypes
      this.enableLineSelection = value.enableLineSelection;
      this.isEligibleForUpload = value.isEligibleForUpload;
    });

    this.form.get('isUrgent')?.valueChanges.subscribe((value: boolean) => {
      this.isUrgent = value;
    });

    this.shellService.subscribeToTopic('serviceline.nomncdenialrationale.change', this.sub);
  }

  getLetterTypes() {
    this.documentsUploadLetterContextService.getLetterTypes(this.tenantId, this.authorization.authorizationId, this.tenantName)
        .subscribe(letterTypes => {
          this.letterTypes = letterTypes;
          this.form.get('letterType')?.setValue('');
    });
  }

  onError(errors: any[]): void {
    this.fileTypeErrors = errors.some((e) => e.type === 'accept');
    this.fileSizeErrors = errors.some((e) => e.type === 'fileSize');
    this.fileNameSizeErrors = errors.some((e) => e.type === 'fileName');
    this.fileNameInvalidErrors = errors.some((e) => e.type === 'fileNameInvalid');
    this.fileTypeErrors
      ? this.notificationService.showError(
          'Please upload only .pdf files.',
          'File format is not valid'
        )
      : null;
    this.fileSizeErrors
      ? this.notificationService.showError(
          'Maximum upload size is 20Mb.',
          'File size is invalid'
        )
      : null;
    this.fileNameSizeErrors
      ? this.notificationService.showError(
          'The file name must not exceed 100 characters. Rename the file and try again.',
          'File name is too long'
        )
      : null;
      this.fileNameInvalidErrors
      ? this.notificationService.showError(
          'The file name contains invalid characters. Please rename the file and try again.',
          'Invalid File Name',
        )
      : null;
    this.resetAttachments();
  }

  addFile(files: FileList | File[]): void {
    this.addSelected(files);
    this.ref.markForCheck();
  }

  async addSelected(files: FileList | File[]) {
    const file: File = files[0];

    if (file.size > this.maxFileSize) {
      const error: any[] = [{ type: 'fileSize', name: file.name }];
      this.onError(error);
    } else {
      this.resetAttachments();

      try {
        const fileBase64: string = (await this.readFileAsync(file)) as string;
        const fileContent = fileBase64.split(',')[1];
        this.attachedFiles = [
          {
            fileContent,
            lineIds: this.selectedLineIds,
            documentFileName: file.name,
            documentType: this.applyDocumentType(),
            isUrgent: this.isUrgent,
            fileMetaData: {
              size: file.size,
              type: file.type,
            },
          },
        ];
      } catch (error) {
        this.notificationService.showError('Error while uploading file.');
      }
    }
  }

  uploadDocuments(): void {
    if (this.attachedFiles.length > 0) {
      this.closeNotes.emit();
      // uploadLetter controllers take different structures, mechanism to map into valid names for each stream
      const mappedAttachedFiles = this.documentsContextService.mapAttachedFiles(this.attachedFiles, {
        tenantId: this.tenantId,
      });
      this.isUploading = true;

      this.documentsContextService
        .uploadLetter(
          this.tenantId,
          this.authorization,
          mappedAttachedFiles[0]
        )
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.resetAttachments();
            this.form.get('isUrgent')?.setValue(false);
            this.form.get('letterType')?.setValue('');
            this.sharedLineSelectComponent?.resetSelected();
            this.filteredLineData = [];
            this.isUploading = false;
          })
        )
        .subscribe({
          next: () => this.uploadLetterComplete.emit(true),
          error: () => this.notificationService.showError(),
        });
    }
  }

  handleSelectedLines(selected: string[]): void {
    this.selectedLineIds = selected;

    // Update attachment if user attaches file before selecting lines.
    if (this.attachedFiles.length > 0) {
      this.attachedFiles[0].lineIds = this.selectedLineIds;
    }
  }

  getNoLineMessage(): string {
    let message = this.documentsUploadLetterContextService.getNoLineMessage(this.selectedLetter, this.tenantName);

    if (!!message) return message;

    switch (this.selectedLetter) {
      case DocumentTypesList.ApprovalLetter:
        message = 'approval ';
        break;
      case DocumentTypesList.ReOpenedApprovalLetter:
        message = 'reopened approval ';
        break;
      case DocumentTypesList.OONApprovalLeter:
        message = 'OON approval ';
        break;
      case DocumentTypesList.IDNLetter:
      case DocumentTypesList.AdverseBenefitDeterminationLetter:
        message = 'denied ';
        break;
      case DocumentTypesList.DismissalLetter:
        message = 'dismissed ';
        break;
      case DocumentTypesList.ExpeditedGrievanceLetter:
        message = 'downgraded ';
        break;
      case DocumentTypesList.DecisionTimeframeExtensionLetter:
        message = 'pending ';
        break;
    }
    return `NOTE: The authorization has no ${message}lines to select.`;
  }

  resetAttachments(): void {
    this.attachedFiles = [];
    this.fileTypeErrors = false;
    this.fileSizeErrors = false;
    this.fileNameSizeErrors = false;
    this.fileNameInvalidErrors = false;
  }

  ngOnDestroy() {
    this.shellService.unsubscribeFromTopic('serviceline.nomncdenialrationale.change', this.sub);
  }

  private readFileAsync(file: File): Promise<string | ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private applyDocumentType(): string {
    if (!this.isHH) {
      return this.selectedLetter;
    }

    return this.letterTypes.find(el => el.documentDisplayName === this.selectedLetter)?.documentType;
  }
}
